import { Box, Card, CardContent, CardHeader, Skeleton, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { TrendingUp, DollarSign, Target, FileText } from "lucide-react"
import type { MonthlyBreakdown } from "@crm-src/types/financial-metrics"
import { formatCurrency } from "@crm-src/lib/utils"

interface SalespersonPerformanceChartsProps {
  monthlyBreakdown: MonthlyBreakdown[]
  isLoading?: boolean
}

/**
 * Component that displays performance charts for a salesperson
 * Shows revenue trends, deals closed, and quotes performance over time
 * Includes both monthly values and running totals
 */
export function SalespersonPerformanceCharts({
  monthlyBreakdown,
  isLoading
}: SalespersonPerformanceChartsProps) {
  // Chart axis/grid/tooltip chrome was driven by --color-* CSS vars; bridge
  // them to the MUI palette now that the vars are gone (Reference A.7). The
  // bright series hex below (#10b981 / #3b82f6 / #8b5cf6) are genuine chart
  // data colors and stay literal.
  const theme = useTheme()
  const axisColor = theme.palette.text.disabled
  const gridColor = theme.palette.divider
  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    // Soft, theme-driven divider so the tooltip reads as a quiet panel
    // rather than a graphite box.
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    padding: '8px'
  }
  const revenueStroke = theme.palette.success.main
  const totalRevenueStroke = theme.palette.primary.main
  const quotesStroke = '#8b5cf6'

  // Calculate total values for charts (running totals)
  const chartData = monthlyBreakdown.map((month, index) => {
    const previousMonths = monthlyBreakdown.slice(0, index)
    const totalRevenue = previousMonths.reduce((sum, m) => sum + (m.revenue || 0), 0) + (month.revenue || 0)
    const totalDeals = previousMonths.reduce((sum, m) => sum + (m.deals_closed || 0), 0) + (month.deals_closed || 0)
    
    return {
      month: month.month,
      revenue: month.revenue || 0,
      totalRevenue,
      dealsClosed: month.deals_closed || 0,
      totalDeals,
      quotesCreated: month.quotes_created || 0,
    }
  })

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardContent sx={{ pt: 3 }}>
              <Skeleton variant="rounded" sx={{ height: 320 }} />
            </CardContent>
          </Card>
        ))}
      </Box>
    )
  }

  if (!monthlyBreakdown || monthlyBreakdown.length === 0) {
    return (
      <Card>
        <CardContent sx={{ pt: 3 }}>
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2, opacity: 0.3 }}><TrendingUp size={48} /></Box>
            <Typography>No performance data available for charting</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: "flex", color: "success.main" }}><DollarSign size={20} /></Box>
              Revenue Trend
            </Box>
          }
          subheader="Track revenue growth over time - both monthly and total revenue"
        />
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorTotalRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="month"
                stroke={axisColor}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                stroke={axisColor}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip
                formatter={(value: any, name: string) => {
                  if (name === 'Monthly Revenue' || name === 'Total Revenue') {
                    return formatCurrency(Number(value), { decimals: 0 })
                  }
                  return value
                }}
                contentStyle={tooltipStyle}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
              />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Monthly Revenue"
                stroke={revenueStroke}
                fillOpacity={1}
                fill="url(#colorRevenue)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="totalRevenue"
                name="Total Revenue"
                stroke={totalRevenueStroke}
                fillOpacity={1}
                fill="url(#colorTotalRevenue)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Deals Closed Chart */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", color: "info.main" }}><Target size={20} /></Box>
                Deals Closed Trend
              </Box>
            }
            subheader="Monthly and total deals closed over time"
          />
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="month"
                  stroke={axisColor}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  stroke={axisColor}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                />
                <Line
                  type="monotone"
                  dataKey="dealsClosed"
                  name="Monthly Deals"
                  stroke={totalRevenueStroke}
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="totalDeals"
                  name="Total Deals"
                  stroke={revenueStroke}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#10b981', r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Quotes Performance Chart */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", color: "#8b5cf6" }}><FileText size={20} /></Box>
                Quotes Created Trend
              </Box>
            }
            subheader="Track quote creation activity over time"
          />
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="month"
                  stroke={axisColor}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  stroke={axisColor}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                />
                <Bar
                  dataKey="quotesCreated"
                  name="Quotes Created"
                  fill={quotesStroke}
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Combined Performance Overview */}
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: "flex", color: "warning.main" }}><TrendingUp size={20} /></Box>
              Performance Overview
            </Box>
          }
          subheader="Combined view of revenue, deals, and quotes performance"
        />
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="month"
                stroke={axisColor}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                yAxisId="left"
                stroke={axisColor}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={axisColor}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: any, name: string) => {
                  if (name === 'Total Revenue') {
                    return formatCurrency(Number(value), { decimals: 0 })
                  }
                  return value
                }}
                contentStyle={tooltipStyle}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="totalRevenue"
                name="Total Revenue"
                stroke={revenueStroke}
                strokeWidth={3}
                dot={{ fill: '#10b981', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="totalDeals"
                name="Total Deals"
                stroke={totalRevenueStroke}
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 3 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="quotesCreated"
                name="Quotes Created"
                stroke={quotesStroke}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#8b5cf6', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  )
}

